import React, { useState, useEffect, useRef, memo, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styles from './FnOPage.module.css';
import { FaRegChartBar } from "react-icons/fa";
import * as api from '../../utils/api';
import { ThemeContext } from '../../context/ThemeContext';
import { formatSymbol } from '../../utils/symbolFormatter';
import { usePrice } from '../../context/PriceContext';

const getOptionsLogoPath = (userSymbol) => {
    const parts = userSymbol.split(' ');
    let underlyingAsset = parts[0];

    if (underlyingAsset.toLowerCase() === 'banknifty') {
        underlyingAsset = 'banknifty';
    } else if (underlyingAsset.toLowerCase() === 'nifty') {
        underlyingAsset = 'nifty';
    } else if (underlyingAsset.toLowerCase() === 'sensex') {
        underlyingAsset = 'sensex';
    } else if (underlyingAsset.toLowerCase() === 'bankex') {
        underlyingAsset = 'bankex';
    }

    return `/logos/${underlyingAsset.toLowerCase()}.svg`;
};

const StockCard = memo(({ symbol, onCardClick }) => {
    const { price: livePrice, lastDayClose } = usePrice(symbol.user_symbol);
    const [priceChange, setPriceChange] = useState(0);
    const [priceChangePercent, setPriceChangePercent] = useState(0);
    const [showImage, setShowImage] = useState(true);

    useEffect(() => {
        if (livePrice !== null && lastDayClose !== null && lastDayClose !== 0) {
            const change = livePrice - lastDayClose;
            const changePercent = (change / lastDayClose) * 100;
            setPriceChange(change);
            setPriceChangePercent(changePercent);
        } else if (livePrice !== null && lastDayClose !== null && lastDayClose === 0) {
            // Handle case where lastDayClose is 0 (e.g. new contract)
            setPriceChange(0);
            setPriceChangePercent(0);
        }
    }, [livePrice, lastDayClose]);

    const displayPrice = livePrice != null ? livePrice.toFixed(2) : '--';
    const getInitials = (name) => (name ? name.charAt(0).toUpperCase() : '');

    const logoPath = symbol.symbol_type === 'F&O'
        ? getOptionsLogoPath(symbol.user_symbol)
        : `/logos/${symbol.user_symbol.toLowerCase().replace(/\s+/g, '_')}.svg`;

    const handleImageError = () => {
        setShowImage(false);
    };

    return (
        <div className={styles['stock-card']} onClick={onCardClick}>
            <div className={styles['stock-logo-container']}>
                {showImage ? (
                    <img
                        src={logoPath}
                        alt={`${symbol.user_symbol} logo`}
                        className={styles['stock-logo']}
                        onError={handleImageError}
                    />
                ) : (
                    <div className={styles['stock-card-initial']}>{getInitials(symbol.user_symbol)}</div>
                )}
            </div>
            <h4>{formatSymbol(symbol.user_symbol)}</h4>
            <p className={styles.price}>₹{displayPrice}</p>
            <p className={`${styles.change} ${priceChange > 0 ? styles.positive : priceChange < 0 ? styles.negative : ''}`}>
                {priceChange.toFixed(2)} ({priceChangePercent.toFixed(2)}%)
            </p>
        </div>
    );
});

const StockRow = ({ symbol, onRowClick, onIconClick, disableLogo }) => {
    const { price: livePrice, lastDayClose } = usePrice(symbol.user_symbol);
    const [priceChange, setPriceChange] = useState(0);
    const [priceChangePercent, setPriceChangePercent] = useState(0);
    const [showImage, setShowImage] = useState(true);

    useEffect(() => {
        if (livePrice !== null && lastDayClose !== null && lastDayClose !== 0) {
            const change = livePrice - lastDayClose;
            const changePercent = (change / lastDayClose) * 100;
            setPriceChange(change);
            setPriceChangePercent(changePercent);
        } else if (livePrice !== null && lastDayClose !== null && lastDayClose === 0) {
            // Handle case where lastDayClose is 0 (e.g. new contract)
            setPriceChange(0);
            setPriceChangePercent(0);
        }
    }, [livePrice, lastDayClose]);

    const displayPrice = livePrice != null ? livePrice.toFixed(2) : '--';
    const getInitials = (name) => (name ? name.charAt(0).toUpperCase() : '');

    const logoPath = disableLogo
        ? null
        : (symbol.symbol_type === 'F&O'
            ? getOptionsLogoPath(symbol.user_symbol)
            : `/logos/${symbol.user_symbol.toLowerCase().replace(/\s+/g, '_')}.svg`);

    const handleImageError = () => {
        setShowImage(false);
    };

    return (
        <div className={styles['stock-row']}>
            <div className={styles['stock-info']} onClick={onRowClick}>
                <div className={styles['stock-logo-container']}>
                    {showImage && logoPath ? (
                        <img
                            src={logoPath}
                            alt={`${symbol.user_symbol} logo`}
                            className={styles['stock-logo']}
                            onError={handleImageError}
                        />
                    ) : (
                        <div className={styles['stock-initial']}>{getInitials(symbol.user_symbol)}</div>
                    )}
                </div>
                <div className={styles['stock-name-and-price']}>
                    <p className={styles['stock-name']}>{formatSymbol(symbol.user_symbol)}</p>
                    <div className={styles['stock-price-info']}>
                        <p className={styles['stock-price']}>₹{displayPrice}</p>
                        <p className={`${styles['stock-change']} ${priceChange > 0 ? styles.positive : priceChange < 0 ? styles.negative : ''}`}>
                            {priceChange.toFixed(2)} ({priceChangePercent.toFixed(2)}%)
                        </p>
                    </div>
                </div>
            </div>
            <div className={styles['chart-icon']} onClick={onIconClick}>
                <FaRegChartBar />
            </div>
        </div>
    );
};

const FnOPage = () => {
    const [indices, setIndices] = useState([]);
    const [fonos, setFonos] = useState([]);
    const [topTradedFono, setTopTradedFono] = useState([]);
    const [loading, setLoading] = useState(true);

    const { theme } = useContext(ThemeContext);
    const topTradedRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const { data: symbols } = await api.getSymbols();
                
                const newIndices = symbols.filter((s) => ['1', '2', '3', '4'].includes(String(s.sr_no)));
                const newFonos = symbols.filter((s) => ['323', '348', '473', '498'].includes(String(s.sr_no)));
                const newTopTradedFono = symbols.filter((s) =>
                    ['322', '323', '324', '347', '348', '349', '472', '473', '474', '672', '673', '674'].includes(String(s.sr_no))
                );

                setIndices(newIndices);
                setFonos(newFonos);
                setTopTradedFono(newTopTradedFono);
                setLoading(false);
            } catch (error) {
                console.error("Error fetching symbols in FnOPage:", error);
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleOpenOptionChain = (symbol) => {
        navigate(`/option-chain/${symbol}`);
    };

    const handleOpenChart = async (symbol) => {
        try {
            await api.updateChartSettings({ chartSymbol: symbol });
            navigate('/trade');
        } catch (error) {
            console.error("Error updating chart settings:", error);
        }
    };

    const handleOpenSymbolInfo = (symbol) => {
        navigate(`/symbol/${symbol}`);
    };

    if (loading) {
        return <div className={styles['fnopage-container']}>Loading...</div>;
    }

    return (
        <div className={`${styles['fnopage-container']} ${theme}`}>
            <div className={styles['section-container']}>
                <div className={styles['header-with-link']}>
                    <h2>Indices</h2>
                </div>
                <div className={styles['scrolling-wrapper']}>
                    {indices.map(idx => (
                        <StockCard 
                            key={idx.user_symbol} 
                            symbol={idx} 
                            onCardClick={() => handleOpenOptionChain(idx.user_symbol)} 
                        />
                    ))}
                </div>
            </div>

            <div className={styles['section-container']}>
                <div className={styles['header-with-link']}>
                    <h2>Options</h2>
                    <Link to="/watchlist?tab=options" className={styles.viewAllLink}>View All</Link>
                </div>
                <div className={styles['scrolling-wrapper']}>
                    {fonos.map(sym => (
                        <StockCard 
                            key={sym.user_symbol} 
                            symbol={sym} 
                            onCardClick={() => handleOpenChart(sym.user_symbol)} 
                        />
                    ))}
                </div>
            </div>

            <div className={styles['section-container']} ref={topTradedRef}>
                <div className={styles['header-with-link']}>
                    <h2>Top Traded</h2>
                </div>
                <div className={styles['top-traded-list']}>
                    {topTradedFono.map(sym => (
                        <StockRow 
                            key={sym.user_symbol} 
                            symbol={sym} 
                            onRowClick={() => handleOpenSymbolInfo(sym.user_symbol)} 
                            onIconClick={() => handleOpenChart(sym.user_symbol)} 
                            disableLogo={true}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default FnOPage;
